import { type TurnId } from "@cmmd-forge/contracts";
import { useEffect, useMemo } from "react";
import { deriveChatViewShortcutLabels, deriveThreadEnvModeState } from "../ChatView.logic";
import { useServerKeybindings } from "~/rpc/serverState";
import { useComposerModeControls } from "./useComposerModeControls";
import { usePersistThreadSettingsForNextTurn } from "./usePersistThreadSettingsForNextTurn";
import { useChatPanelController } from "./useChatPanelController";
import { useChatScrollControls } from "./useChatScrollControls";
import { useChatTerminalController } from "./useChatTerminalController";
import { useChatWorkspaceContext } from "./useChatWorkspaceContext";
import { type useChatRouteRuntime } from "./useChatRouteRuntime";
import { type useChatThreadRuntimeState } from "./useChatThreadRuntimeState";

export function useChatThreadControlsRuntime(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
  readonly onDiffPanelOpen: (() => void) | undefined;
}) {
  const workspace = useChatWorkspaceRuntime(input);
  const shortcuts = useChatShortcutRuntime(input.routeRuntime);
  const panels = useChatPanelsRuntime({ ...input, workspace });
  const terminal = useChatTerminalRuntime({ ...input, workspace, panels, shortcuts });
  const composerModes = useChatComposerModeRuntime(input.routeRuntime);
  const persistence = usePersistThreadSettingsForNextTurn({
    environmentId: input.routeRuntime.route.environmentId,
    serverThread: input.routeRuntime.bootstrap.serverThread,
  });
  const scroll = useChatScrollRuntime({ routeRuntime: input.routeRuntime, panels });
  useCheckpointResetOnThreadChange(input.routeRuntime);
  const env = useChatEnvRuntime({ routeRuntime: input.routeRuntime, workspace });

  return { workspace, shortcuts, panels, terminal, composerModes, persistence, scroll, env };
}

function useChatWorkspaceRuntime(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
}) {
  const { environmentId } = input.routeRuntime.route;
  const { activeThread } = input.routeRuntime.bootstrap;
  const { activeProject } = input.threadRuntime.projectContext;
  return useChatWorkspaceContext({ environmentId, activeThread, activeProject });
}

function useChatShortcutRuntime(routeRuntime: ReturnType<typeof useChatRouteRuntime>) {
  const keybindings = useServerKeybindings();
  const labels = useMemo(
    () =>
      deriveChatViewShortcutLabels({
        keybindings,
        terminalOpen: Boolean(routeRuntime.bootstrap.terminalState.terminalOpen),
      }),
    [keybindings, routeRuntime.bootstrap.terminalState.terminalOpen],
  );
  return { keybindings, labels };
}

function useChatPanelsRuntime(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
  readonly workspace: ReturnType<typeof useChatWorkspaceContext>;
  readonly onDiffPanelOpen: (() => void) | undefined;
}) {
  const { route, navigate, rawSearch, bootstrap } = input.routeRuntime;
  const { activity, projectContext } = input.threadRuntime;
  return useChatPanelController({
    navigate,
    rawSearch,
    routeKind: route.routeKind,
    draftId: route.draftId,
    environmentId: route.environmentId,
    threadId: route.threadId,
    activeThreadKey: bootstrap.activeThreadKey,
    activeThread: bootstrap.activeThread,
    isServerThread: bootstrap.isServerThread,
    isLocalDraftThread: bootstrap.isLocalDraftThread,
    canOpenRightSidebar: bootstrap.canOpenRightSidebar,
    activePlan: activity.activePlan as { readonly turnId?: TurnId } | null,
    sidebarProposedPlanTurnId: activity.sidebarProposedPlan?.turnId,
    activeTaskContextExists: Boolean(input.workspace.activeTaskContextModel),
    latestTurnId: projectContext.activeLatestTurn?.turnId ?? null,
    onDiffPanelOpen: input.onDiffPanelOpen,
  });
}

function useChatTerminalRuntime(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
  readonly workspace: ReturnType<typeof useChatWorkspaceContext>;
  readonly panels: ReturnType<typeof useChatPanelController>;
  readonly shortcuts: ReturnType<typeof useChatShortcutRuntime>;
}) {
  const { route, bootstrap, composer, runtimeFlags } = input.routeRuntime;
  return useChatTerminalController({
    environmentId: route.environmentId,
    routeThreadRef: bootstrap.routeThreadRef,
    serverThreadStatus: bootstrap.serverThreadStatus,
    activeThreadId: bootstrap.activeThreadId,
    activeThreadRef: bootstrap.activeThreadRef,
    activeThreadKey: bootstrap.activeThreadKey,
    activeThread: bootstrap.activeThread,
    activeProject: input.threadRuntime.projectContext.activeProject ?? null,
    activeProjectCwd: input.workspace.activeProjectCwd,
    activeThreadWorktreePath: input.workspace.activeThreadWorktreePath,
    gitCwd: input.workspace.gitCwd,
    terminalState: bootstrap.terminalState,
    keybindings: input.shortcuts.keybindings,
    canvasOpen: input.panels.canvasOpen,
    focusComposer: composer.focusComposer,
    setThreadError: bootstrap.setThreadError,
    setLastInvokedScriptByProjectId: runtimeFlags.setLastInvokedScriptByProjectId,
    onToggleDiff: input.panels.onToggleDiff,
    onToggleCanvas: input.panels.onToggleCanvas,
    onTogglePreview: input.panels.onTogglePreview,
    togglePlanSidebar: input.panels.togglePlanSidebar,
  });
}

function useChatComposerModeRuntime(routeRuntime: ReturnType<typeof useChatRouteRuntime>) {
  const { bootstrap, composer } = routeRuntime;
  return useComposerModeControls({
    composerDraftTarget: bootstrap.composerDraftTarget,
    isLocalDraftThread: bootstrap.isLocalDraftThread,
    runtimeMode: bootstrap.runtimeMode,
    interactionMode: bootstrap.interactionMode,
    setComposerDraftRuntimeMode: composer.setComposerDraftRuntimeMode,
    setComposerDraftInteractionMode: composer.setComposerDraftInteractionMode,
    setDraftThreadContext: composer.setDraftThreadContext,
    scheduleComposerFocus: composer.scheduleComposerFocus,
  });
}

function useChatScrollRuntime(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly panels: ReturnType<typeof useChatPanelController>;
}) {
  return useChatScrollControls({
    activeThreadId: input.routeRuntime.bootstrap.activeThreadId,
    terminalOpen: Boolean(input.routeRuntime.bootstrap.terminalState.terminalOpen),
    focusComposer: input.routeRuntime.composer.focusComposer,
    onTimelineReachedEnd: input.routeRuntime.composer.restoreComposerAfterTimelineReachedEnd,
    onThreadReset: input.panels.resetPlanAutoOpen,
  });
}

function useCheckpointResetOnThreadChange(routeRuntime: ReturnType<typeof useChatRouteRuntime>) {
  const { activeThread } = routeRuntime.bootstrap;
  const { setIsRevertingCheckpoint } = routeRuntime.runtimeFlags;
  useEffect(() => {
    setIsRevertingCheckpoint(false);
  }, [activeThread?.id, setIsRevertingCheckpoint]);
}

function useChatEnvRuntime(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly workspace: ReturnType<typeof useChatWorkspaceContext>;
}) {
  const { activeThread, isServerThread, isLocalDraftThread, draftThread } =
    input.routeRuntime.bootstrap;
  const envLocked = Boolean(
    activeThread &&
    (activeThread.messages.length > 0 ||
      (activeThread.session !== null && activeThread.session.status !== "closed")),
  );
  return deriveThreadEnvModeState({
    activeThread,
    isServerThread,
    isLocalDraftThread,
    draftThreadEnvMode: draftThread?.envMode,
    pendingServerThreadEnvMode: null,
    pendingServerThreadBranch: null,
    defaultBranch: input.workspace.gitStatusQuery.data?.defaultBranch ?? null,
    envLocked,
    isGitRepo: input.workspace.isGitRepo,
    gitStatusKnown: input.workspace.gitStatusKnown,
  });
}
